import { useEffect, useState } from 'react';
import { getProfileView, getProfileByAlias, type FilteredProfile } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import '../styles/ProfileVisibility.css';

interface Props {
  /** Either a Firebase UID or an alias (prefixed with "@"). */
  uid?: string;
  alias?: string;
}

const FIELD_LABELS: Record<string, string> = {
  realName:      'असली नाम',
  age:           'उम्र',
  pincode:       'पिनकोड',
  timeZone:      'समय क्षेत्र',
  qualification: 'योग्यता',
  rating:        'रेटिंग',
  studyAreas:    'अध्ययन क्षेत्र',
  availability:  'उपलब्धता',
  sessionModes:  'सत्र माध्यम',
  lookingFor:    'खोज रहे हैं',
};

const ALWAYS_SHOWN = ['profileKind', 'learningLevel', 'nativeLanguage', 'preferredScript', 'collaborationMode'];

const PROFILE_KIND_LABELS: Record<string, string> = {
  learner:            'शिक्षार्थी',
  knowledge_provider: 'ज्ञान-प्रदाता',
  both:               'दोनों',
};

const COLLAB_LABELS: Record<string, string> = {
  sync:   'समकालिक',
  async:  'अतुल्यकालिक',
  either: 'दोनों',
};

function formatFieldValue(key: string, value: unknown): string {
  if (Array.isArray(value)) return value.join(', ');
  if (key === 'profileKind') return PROFILE_KIND_LABELS[String(value)] ?? String(value);
  if (key === 'collaborationMode') return COLLAB_LABELS[String(value)] ?? String(value);
  return String(value ?? '');
}

export default function PublicProfileView({ uid, alias }: Props) {
  const { user } = useAuth();

  const [profile,  setProfile]  = useState<FilteredProfile | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setErrorMsg('');
      try {
        const idToken = user ? await user.getIdToken() : undefined;
        let resolvedUid = uid;

        if (!resolvedUid && alias) {
          const lookup = await getProfileByAlias(alias);
          resolvedUid = lookup.uid;
        }
        if (!resolvedUid) {
          setErrorMsg('प्रोफ़ाइल नहीं मिली।');
          setLoading(false);
          return;
        }
        const p = await getProfileView(resolvedUid, idToken);
        if (!cancelled) setProfile(p);
      } catch {
        if (!cancelled) setErrorMsg('प्रोफ़ाइल नहीं मिली।');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [uid, alias, user]);

  if (loading) return <div className="ppv-loading">लोड हो रहा है…</div>;
  if (errorMsg) return <div className="ppv-error">{errorMsg}</div>;
  if (!profile)  return null;

  const fieldStates = profile.fieldStates ?? {};

  return (
    <div className="ppv-root">
      {/* Header */}
      <div className="ppv-header">
        <div className="ppv-avatar" aria-hidden="true">
          {(profile.alias || '?').charAt(0).toUpperCase()}
        </div>
        <div className="ppv-header-info">
          <h1 className="ppv-alias">@{profile.alias}</h1>
          <span className="ppv-kind">{PROFILE_KIND_LABELS[profile.profileKind] ?? profile.profileKind}</span>
        </div>
      </div>

      {/* Always-visible fields */}
      <dl className="ppv-dl">
        {ALWAYS_SHOWN.map(key => {
          const val = (profile as unknown as Record<string, unknown>)[key];
          if (val == null || val === '') return null;
          return (
            <div className="ppv-row" key={key}>
              <dt>{FIELD_LABELS[key] ?? key}</dt>
              <dd>{formatFieldValue(key, val)}</dd>
            </div>
          );
        })}

        {/* Controlled fields — show value or lock icon */}
        {Object.keys(FIELD_LABELS).map(key => {
          const state = fieldStates[key];
          if (!state) return null;
          const val = (profile as unknown as Record<string, unknown>)[key];
          return (
            <div className={`ppv-row ${state === 'hidden' ? 'ppv-row--locked' : ''}`} key={key}>
              <dt>{FIELD_LABELS[key]}</dt>
              <dd>
                {state === 'hidden' ? <span className="ppv-lock" title="गोपनीय">🔒</span>
                  : state === 'empty' ? <em className="ppv-empty">—</em>
                  : formatFieldValue(key, val)}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
